import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';

export default function WhySmarterSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const pillars = t('whySmarter.pillars');

  // Split body text on newlines for paragraph rendering
  const bodyParagraphs = (t('whySmarter.body') || '').split('\n\n').filter(Boolean);

  return (
    <section
      id="why-smarter"
      className="relative pt-20 pb-10 sm:pt-28 sm:pb-14 px-6 sm:px-12 lg:px-24"
      data-testid="why-smarter-section"
    >
      {/* Subtle background glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1A2A1A]/30 to-transparent pointer-events-none" />

      <div className="relative max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left: heading + body */}
          <motion.div
            ref={sectionRef}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium leading-snug mb-8">
              {t('whySmarter.heading')}{' '}
              <span className="text-[#BDD6B5]">{t('whySmarter.headingGreen')}</span>
            </h2>

            <div className="space-y-5">
              {bodyParagraphs.map((para, i) => (
                <p key={i} className="text-base text-white/78 leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Right: three value pillars */}
          <div className="space-y-4 lg:pt-16">
            {Array.isArray(pillars) && pillars.map((pillar, i) => {
              const PillarCard = () => {
                const ref = useRef(null);
                const inView = useInView(ref, { once: true, margin: '-40px' });
                return (
                  <motion.div
                    ref={ref}
                    initial={{ opacity: 0, x: 30 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: i * 0.14 }}
                    className="glass-card p-6 sm:p-7 relative overflow-hidden group"
                    data-testid={`smarter-pillar-${i}`}
                  >
                    {/* Accent left border */}
                    <div className="absolute left-0 top-4 bottom-4 w-[2px] bg-gradient-to-b from-transparent via-[#BDD6B5]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Step indicator */}
                    <span className="font-mono text-[10px] tracking-widest uppercase text-[#BDD6B5]/50 block mb-2">
                      {String(i + 1).padStart(2, '0')}
                    </span>

                    <h3 className="text-base font-semibold text-[#BDD6B5] mb-1.5">
                      {pillar.label}
                    </h3>
                    <p className="text-sm text-white/78 leading-relaxed">
                      {pillar.desc}
                    </p>
                  </motion.div>
                );
              };
              return <PillarCard key={i} />;
            })}
          </div>

        </div>
      </div>
    </section>
  );
}
